<template>
  <header class="sidebar-header">
    <div class="brand-mark">
      <img :src="logoUrl" :alt="appName" class="sidebar-logo" />
    </div>
    <div class="sidebar-brand">
      <strong class="brand-name">{{ appName }}</strong>
      <span class="brand-tagline">{{ t('Freight operations') }}</span>
    </div>
    <button type="button" class="sidebar-close" :aria-label="t('Close navigation')" @click="onMenuToggle">
      <i class="pi pi-times" />
    </button>
  </header>

  <div class="sidebar-tools">
    <label class="menu-search">
      <span class="sr-only">{{ t('Search navigation') }}</span>
      <i class="pi pi-search" />
      <input
        v-model.trim="menuSearch"
        type="search"
        autocomplete="off"
        :placeholder="t('Find a page...')"
        :aria-label="t('Search navigation')"
      />
      <button v-if="menuSearch" type="button" :aria-label="t('Clear navigation search')" @click="menuSearch = ''">
        <i class="pi pi-times" />
      </button>
      <kbd v-else>/</kbd>
    </label>
  </div>

  <nav id="app-navigation" class="menu-container" :aria-label="t('Main navigation')">
    <div v-if="menuSearch && !visibleModel.length" class="menu-empty">
      <i class="pi pi-search" />
      <strong>{{ t('No page found') }}</strong>
      <span>{{ t('Try another name or keyword.') }}</span>
      <button type="button" @click="menuSearch = ''">{{ t('Clear search') }}</button>
    </div>

    <ul v-else class="layout-menu">
      <template v-for="(item, index) in visibleModel" :key="item.label">
        <AppMenuItem v-if="!item.separator" :item="item" :index="index" />
        <li v-else class="menu-separator" />
      </template>
    </ul>
  </nav>

  <footer class="sidebar-footer">
    <router-link to="/guide" class="sidebar-guide" @click="closeMobileMenu">
      <span class="sidebar-guide-icon"><i class="pi pi-question-circle" /></span>
      <span class="sidebar-guide-copy">
        <strong>{{ t('Step-by-step guide') }}</strong>
        <small>{{ t('Registration to final delivery') }}</small>
      </span>
      <i class="pi pi-chevron-right sidebar-guide-arrow" />
    </router-link>

    <div class="sidebar-footer-content">
      <div class="footer-avatar" aria-hidden="true">{{ userInitial }}</div>
      <div class="footer-info">
        <strong class="footer-name">{{ userName }}</strong>
        <span class="footer-role">{{ userRole }}</span>
      </div>
      <router-link
        v-if="can('system.settings.manage')"
        to="/settings"
        class="footer-settings"
        :aria-label="t('Open settings')"
        :title="t('Settings')"
        @click="closeMobileMenu"
      >
        <i class="pi pi-cog" />
      </router-link>
    </div>
  </footer>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useLayout } from '@/layout/composables/layout';
import { useCompanyBrand } from '@/composables/useCompanyBrand';
import { usePermissions } from '@/composables/usePermissions';
import { useAppI18n } from '@/i18n';
import AppMenuItem from './AppMenuItem.vue';
import { navigationModel } from './navigationModel';

const { layoutConfig, layoutState, onMenuToggle } = useLayout();
const { can, credentials, isAdmin } = usePermissions();
const { locale, t } = useAppI18n();
const { settings: appSettings, companyName: appName, companyLogoUrl } = useCompanyBrand();
const menuSearch = ref('');

const logoUrl = computed(() => {
  if (companyLogoUrl.value) return companyLogoUrl.value;
  return `/layout/images/${layoutConfig.darkTheme.value ? 'logo-white' : 'logo-dark'}.svg`;
});

const userName = computed(() => credentials.value.name || t('Signed-in user'));
const userInitial = computed(() => userName.value.trim().charAt(0).toUpperCase() || 'U');
const userRole = computed(() => {
  if (isAdmin.value) return t('Administrator');
  return credentials.value.role?.name || t('Operations team');
});

const filterMenuItems = (items) =>
  items
    .filter(
      (item) =>
        item.visibleInMenu !== false &&
        (!item.feature || appSettings.value[item.feature] !== false) &&
        (!item.permission || can(item.permission))
    )
    .map((item) => ({
      ...item,
      items: item.items ? filterMenuItems(item.items) : undefined
    }))
    .filter((item) => !item.items || item.items.length > 0);

const localizeMenuItems = (items) =>
  items.map((item) => {
    const sourceLabel = item.label;
    const sourceDescription = item.description || '';
    const localizedLabel = t(sourceLabel);
    const localizedDescription = sourceDescription ? t(sourceDescription) : undefined;

    return {
      ...item,
      label: localizedLabel,
      description: localizedDescription,
      searchTerms: [sourceLabel, localizedLabel, sourceDescription, localizedDescription, item.keywords]
        .filter(Boolean)
        .join(' '),
      items: item.items ? localizeMenuItems(item.items) : undefined
    };
  });

const collectSearchResults = (groups, query) => {
  const results = [];

  const visit = (items, path = []) => {
    items.forEach((item) => {
      const nextPath = [...path, item.label];
      if (item.items) {
        visit(item.items, nextPath);
        return;
      }

      const searchText = [item.searchTerms, ...path].filter(Boolean).join(' ').toLocaleLowerCase(locale.value);
      if (searchText.includes(query)) {
        results.push({
          ...item,
          description: path.join(' / '),
          items: undefined
        });
      }
    });
  };

  visit(groups);
  return results;
};

const visibleModel = computed(() => {
  const accessible = localizeMenuItems(filterMenuItems(navigationModel));
  const query = menuSearch.value.toLocaleLowerCase(locale.value).trim();
  if (!query) return accessible;

  const matches = collectSearchResults(accessible, query);
  return matches.length ? [{ label: t(`Search results - ${matches.length}`), items: matches }] : [];
});

const focusMenuSearch = (event) => {
  const target = event.target;
  const isTyping =
    target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target?.isContentEditable;
  if (event.key === '/' && !isTyping) {
    event.preventDefault();
    document.querySelector('.menu-search input')?.focus();
  }
};

const closeMobileMenu = () => {
  if (layoutState.staticMenuMobileActive.value || layoutState.overlayMenuActive.value) {
    onMenuToggle();
  }
};

onMounted(() => {
  window.addEventListener('keydown', focusMenuSearch);
});

onBeforeUnmount(() => {
  window.removeEventListener('keydown', focusMenuSearch);
});
</script>
